import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '@SonarCX/store'

import { getMedia } from '@SonarCX/lib/api'
import { getURLFromBlob } from '@SonarCX/utilities'

const MEDIA_RESOLUTION = 140
// web workers doesn't support images or canvas. sad.
const canvas = document.createElement('canvas')
const ctx = canvas.getContext('2d')

type MediaCacheState = {
  status: Record<string, 'DONE' | 'FETCHING'>
  media: Record<string, string>
  finishedAnimations: string[]
}

const initialState: MediaCacheState = {
  status: {},
  media: {},
  finishedAnimations: []
}

const mediaCacheSlice = createSlice({
  name: 'mediaCache',
  initialState,
  reducers: {
    startLoad: (state: MediaCacheState, action: PayloadAction<string>) => {
      state.status[action.payload] = 'FETCHING'
    },

    finishLoad: (
      state: MediaCacheState,
      action: PayloadAction<{ id: string; url: string }>
    ) => {
      const { id, url } = action.payload
      state.status[id] = 'DONE'
      state.media[id] = url
    },

    finishAnimation: (
      state: MediaCacheState,
      action: PayloadAction<string>
    ) => {
      const id = action.payload

      if (state.finishedAnimations.includes(id)) {
        return
      }

      state.finishedAnimations.push(id)
    }
  }
})

const { startLoad, finishLoad } = mediaCacheSlice.actions
export const { finishAnimation } = mediaCacheSlice.actions

export const loadMedia = (id: string, resize?: boolean) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    if (!id) {
      return
    }
    const { status } = selectMediaCache(getState())

    if (id in status) {
      return
    }

    try {
      dispatch(startLoad(id))

      const blob = await getMedia(id)

      let url = ''
      if (blob) {
        url = await getURLFromBlob(blob)
      }

      if (!resize) {
        dispatch(finishLoad({ id, url }))
        return
      }
      // TODO: This is a temporary solution to resize images, this should be done on the backend or on profile pic upload.

      try {
        // this isn't the best solution, its a workaround for now.
        const img = new Image()
        img.src = url
        img.onload = () => {
          canvas.width = MEDIA_RESOLUTION
          canvas.height = MEDIA_RESOLUTION
          ctx?.drawImage(img, 0, 0, MEDIA_RESOLUTION, MEDIA_RESOLUTION)
          url = canvas.toDataURL()
          dispatch(finishLoad({ id, url }))
        }
      } catch (err) {
        console.error('Error resizing image', err)
        dispatch(finishLoad({ id, url }))
      }
    } catch (err) {
      console.error('Error fetching media in slice', err)
    }
  }
}

export const selectMediaCache = (state: RootState) => state.mediaCache

export default mediaCacheSlice.reducer
